import React, { useState } from "react";
import { Badge, Box, Collapse, Flex, HStack, Text } from "@chakra-ui/react";
import { ClockCounterClockwise } from "@phosphor-icons/react";
import { RightSidebarHeader } from "components/Layout/RightSidebarWidget";

// FRONTEND MOCK ONLY — no real data behind this yet. Wired to the "History" link on the Commit tab's
// header (CommitPanel.jsx's CommitHeader). Shape follows AiIngestHistory.jsx (the equivalent list on the
// task page's own Methodology card: run row -> target, row count, source file, date, expandable trail),
// so swapping the placeholder rows below for `useAiIngestRuns`/`runsForScope` later is a drop-in, not a
// redesign.
const MOCK_RUNS = [
  {
    id: "mock-3",
    rowsWritten: 39,
    rowsReplaced: 0,
    sourceFileName: "Meridian_FY27_Stationary_Source_Data_Simple.xlsx",
    createdDate: "Just now",
    trailPreview:
      "Filtered out Milton Keynes and Diesel rows, increased the value column by 25%, then added the " +
      "Tilbury and Milton Keynes rows back in.",
  },
  {
    id: "mock-2",
    rowsWritten: 62,
    rowsReplaced: 62,
    sourceFileName: "Meridian_FY27_Stationary_Source_Data.xlsx",
    createdDate: "21 Sept 2026",
    trailPreview: "Replaced the section's existing rows with a corrected mapping of the same source file.",
  },
  {
    id: "mock-1",
    rowsWritten: 58,
    rowsReplaced: 0,
    sourceFileName: "Meridian_FY26_Stationary_Source_Data.xlsx",
    createdDate: "3 Jun 2026",
    trailPreview: "First AI-assisted ingest into this section.",
  },
];

/**
 * Right-sidebar tray behind the Commit tab's "History" link — a mocked placeholder for the list of past
 * commits into this task section. No backend call: `MOCK_RUNS` above stands in for what a real
 * `useAiIngestRuns`/`runsForScope(runs, scopeKey)` query (see AiIngestHistory.jsx, the same list already
 * built for the task page) would return.
 */
export default function CommitHistoryPanel({ targetLabel }) {
  const [openId, setOpenId] = useState(null);

  return (
    <Box>
      <RightSidebarHeader Icon={ClockCounterClockwise}>Commit history</RightSidebarHeader>
      <Text fontSize="12px" color="gray.500" mt={1} mb={4}>
        {targetLabel ? `Past commits into ${targetLabel}.` : "Past commits into this section."}
      </Text>

      <Box display="flex" flexDirection="column" gap="8px">
        {MOCK_RUNS.map((run) => (
          <MockRunRow
            key={run.id}
            run={run}
            isOpen={openId === run.id}
            onToggle={() => setOpenId(openId === run.id ? null : run.id)}
          />
        ))}
      </Box>

      <Text fontSize="11px" color="gray.400" mt={4}>
        Placeholder data — not yet wired to this task&apos;s real ingest history.
      </Text>
    </Box>
  );
}

const MockRunRow = ({ run, isOpen, onToggle }) => (
  <Box border="1px solid" borderColor="gray.150" borderRadius="10px" bg="white" overflow="hidden">
    <Flex
      as="button"
      type="button"
      onClick={onToggle}
      w="100%"
      align="center"
      justify="space-between"
      px="14px"
      py="10px"
      gap={3}
      textAlign="left"
      _hover={{ bg: "gray.50" }}
    >
      <HStack spacing={3} minW={0}>
        <Text fontSize="13px" fontWeight="600" color="darkTeal" noOfLines={1}>
          {run.rowsWritten} {run.rowsWritten === 1 ? "row" : "rows"}
          {run.rowsReplaced > 0 && ` (replaced ${run.rowsReplaced})`}
        </Text>
        <Text fontSize="12px" color="gray.500" noOfLines={1}>
          {run.sourceFileName}
        </Text>
      </HStack>
      <HStack spacing={3} flexShrink={0}>
        <Text fontSize="11px" color="gray.500">
          {run.createdDate}
        </Text>
        <Badge fontSize="10px" variant="subtle" bg="gray.100" color="gray.600" borderRadius="full" px={2}>
          {isOpen ? "Hide" : "Read"}
        </Badge>
      </HStack>
    </Flex>
    <Collapse in={isOpen} animateOpacity>
      <Box px="14px" pb="14px">
        <Box bg="gray.50" borderRadius="8px" p="14px" fontSize="12px" color="gray.700">
          {run.trailPreview}
        </Box>
      </Box>
    </Collapse>
  </Box>
);
